import React, { useCallback, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { Col, Row } from "react-bootstrap";
import { Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Papa from "papaparse";

import config from "../../lib/config";
import { currentSession } from "../../lib/auth-session";
import { fetchImageAsDataUrl } from "../../lib/fetchImageAsDataUrl";
import { randomAlphanumeric } from "../../lib/randomId";

import BasicFields from "./components/BasicFields.jsx";
import ChallengeSwitches from "./components/ChallengeSwitches.jsx";
import CalendarFields from "./components/CalendarFields.jsx";
import EnterAsYouGoFields from "./components/EnterAsYouGoFields.jsx";
import ChallengeItemsList from "./components/ChallengeItemsList.jsx";
import CategoryItemsList from "./components/CategoryItemsList.jsx";
import ImageSection from "./components/ImageSection.jsx";
import ItemInfoModal from "./components/ItemInfoModal.jsx";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function extractInfo(infoString) {
    if (infoString.join().length > 9) {
        const csvArray = infoString;
        const extraInfo = {};
        if (csvArray[0].length > 0) extraInfo.Description = csvArray[0];
        if (csvArray[1].length > 0) extraInfo.URL = csvArray[1];
        if (csvArray[2].length > 0) extraInfo.URLText = csvArray[2];
        if (csvArray[3].length > 0) extraInfo.MapsLink = csvArray[3];
        if (csvArray[4].length > 0 && csvArray[5].length > 0)
            extraInfo.misc = [{ index: 0, key: csvArray[4], value: csvArray[5] }];
        if (csvArray[6].length > 0 && csvArray[7].length > 0)
            extraInfo.misc = (extraInfo.misc ?? []).concat({ index: 1, key: csvArray[6], value: csvArray[7] });
        if (csvArray[8].length > 0 && csvArray[9].length > 0)
            extraInfo.misc = (extraInfo.misc ?? []).concat({ index: 2, key: csvArray[8], value: csvArray[9] });
        return extraInfo;
    }
    return null;
}

function newItem() {
    return { item: "", id: randomAlphanumeric(10) };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const CreateChallenge = () => {
    const params = useParams();

    // ---- identity / routing ----
    const [redirect, setRedirect] = useState(false);
    const [ChallengeId, setChallengeId] = useState(params.id);
    const [ChallengeLoaded, setChallengeLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(!!params.id);

    // ---- form refs (uncontrolled fields) ----
    const challengeNameRef = useRef(null);
    const descriptionRef = useRef(null);
    const markdownRef = useRef(null);
    const placeholderRef = useRef(null);
    const inputCountRef = useRef(null);
    const modalRef = useRef(null);

    // ---- challenge state ----
    const [challenge, setChallenge] = useState({
        ChallengeId: "",
        Name: "",
        Description: "",
        Markdown: "",
        MetricDescription: "",
        ChallengeType: "BASIC",
        ProgressType: "fraction",
        IsActive: true,
        IsReminder: true,
        IsSearchable: false,
        IsExpired: false,
        IsLocked: false,
        IsLockedLimit: null,
        LockedChallengePassword: null,
        Affiliate: false,
        LiveAt: new Date(),
        ChallengeItems: [newItem()],
        CategoryItems: [],
        Interval: "DAILY",
        Image: "",
        Categories: [],
    });

    const [challengeItemsBackup, setChallengeItemsBackup] = useState([newItem()]);
    const [categoryItemsBackup, setCategoryItemsBackup] = useState([
        { Category: "", ChallengeItems: [newItem()] },
    ]);

    // ---- locked state ----
    const [lockedLimit, setLockedLimit] = useState(0);
    const [lockedLimitEditable, setLockedLimitEditable] = useState(false);
    const [lockedChallengePassword, setLockedChallengePassword] = useState(null);
    const [passcodes, setPasscodes] = useState(null);

    // ---- calendar-specific state ----
    const [halfWayCount, setHalfWayCount] = useState(null);
    const [threeQuarterCount, setThreeQuarterCount] = useState(null);
    const [completeCount, setCompleteCount] = useState(null);
    const [completeMetricCount, setCompleteMetricCount] = useState(null);
    const [metricDescription, setMetricDescription] = useState(null);

    // ---- image state ----
    const [image, setImage] = useState([]);

    // ---- description length ----
    const [descriptionLength, setDescriptionLength] = useState(0);

    // ---- item info modal state ----
    const [validated] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [infoIndex, setInfoIndex] = useState(null);
    const [infoCategoryIndex, setInfoCategoryIndex] = useState(null);
    const [info, setInfo] = useState({});
    const [extraInfo, setExtraInfo] = useState([]);

    // ---------------------------------------------------------------------------
    // Load existing challenge
    // ---------------------------------------------------------------------------
    currentSession()
        .then(user => {
            if (ChallengeId && !ChallengeLoaded) {
                fetch(`${config.BASE_URL}/challenge/${ChallengeId}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user.getIdToken().getJwtToken()}`,
                    },
                })
                    .then(res => {
                        setChallengeLoaded(true);
                        setIsLoading(false);
                        if (res.status !== 200) {
                            alert("Error, Challenge not found");
                            return;
                        }
                        res.json().then(response => {
                            setChallengeId(response.ChallengeId);
                            fetchImageAsDataUrl(
                                `${config.IMAGE_BASE}/${response.ChallengeId}/main.jpg`
                            ).then(src => {
                                if (src && src !== "data:application/octet-stream;base64,") {
                                    setImage([{ id: crypto.randomUUID(), src }]);
                                }
                            });

                            if (response.ChallengeType === "CATEGORIES") {
                                setChallenge({ ...response, ChallengeItems: [], LiveAt: new Date(response.LiveAt) });
                            } else {
                                setChallenge({ ...response, CategoryItems: [], LiveAt: new Date(response.LiveAt) });
                            }

                            if (challengeNameRef.current)
                                challengeNameRef.current.value = response.Name;
                            if (descriptionRef.current)
                                descriptionRef.current.value = response.Description;
                            if (markdownRef.current && response.Markdown)
                                markdownRef.current.value = response.Markdown;

                            if (response.MetricDescription) setMetricDescription(response.MetricDescription);
                            if (response.Metric) setCompleteMetricCount(response.Metric);
                            if (response.HalfWayCount) setHalfWayCount(response.HalfWayCount);
                            if (response.ThreeQuarterCount) setThreeQuarterCount(response.ThreeQuarterCount);
                            if (response.CompleteCount) setCompleteCount(response.CompleteCount);

                            if (response.ChallengeType === "ENTERASYOUGO") {
                                if (placeholderRef.current)
                                    placeholderRef.current.value = response.Placeholder;
                                if (inputCountRef.current)
                                    inputCountRef.current.value = response.InputCount;
                            }

                            if (response.IsLockedLimit) {
                                setLockedLimit(response.IsLockedLimit);
                                setLockedLimitEditable(true);
                            }

                            if (response.IsLocked && response.IsLockedLimit == null) {
                                currentSession()
                                    .then(u => {
                                        fetch(
                                            `${config.BASE_URL}/challenge/passcode/${ChallengeId}`,
                                            {
                                                method: "GET",
                                                headers: {
                                                    "Content-Type": "application/json",
                                                    Authorization: `Bearer ${u.getIdToken().getJwtToken()}`,
                                                },
                                            }
                                        )
                                            .then(r => r.json())
                                            .then(r => {
                                                if (!r.unique) {
                                                    setLockedChallengePassword(r.Passcode);
                                                    setLockedLimitEditable(true);
                                                }
                                            })
                                            .catch(console.log);
                                    })
                                    .catch(console.log);
                            } else if (response.IsLocked && response.IsLockedLimit != null) {
                                currentSession()
                                    .then(u => {
                                        fetch(
                                            `${config.BASE_URL}/challenge/passcodes/${ChallengeId}`,
                                            {
                                                method: "GET",
                                                headers: {
                                                    "Content-Type": "application/json",
                                                    Authorization: `Bearer ${u.getIdToken().getJwtToken()}`,
                                                },
                                            }
                                        )
                                            .then(r => r.json())
                                            .then(r => setPasscodes(r))
                                            .catch(console.log);
                                    })
                                    .catch(console.log);
                            }
                        });
                    })
                    .catch(err => {
                        setIsLoading(false);
                        console.log(err);
                    });
            }
        })
        .catch(() => {
            toast.error("Not logged in — redirecting to login.");
            setRedirect(true);
        });

    // ---------------------------------------------------------------------------
    // Submit
    // ---------------------------------------------------------------------------
    const handleSubmit = event => {
        event.preventDefault();
        if (!event.currentTarget.checkValidity()) {
            event.stopPropagation();
            return;
        }

        const newChallenge = {
            Categories: challenge.Categories,
            ChallengeId: challenge.ChallengeId,
            ChallengeType: challenge.ChallengeType,
            IsActive: challenge.IsActive,
            IsReminder: challenge.IsReminder,
            IsSearchable: challenge.IsSearchable,
            IsExpired: challenge.IsExpired ?? false,
            Affiliate: challenge.Affiliate ?? false,
            IsLocked: challenge.IsLocked ?? false,
            ProgressType: challenge.ProgressType,
            Name: challengeNameRef.current.value,
            Description:
                descriptionRef.current.value.length > 0 ? descriptionRef.current.value : " ",
            LiveAt: challenge.LiveAt.toISOString(),
        };

        if (lockedLimit > 0) newChallenge.IsLockedLimit = lockedLimit;
        if (
            lockedChallengePassword != null &&
            challenge.IsLocked &&
            (lockedLimit == null || lockedLimit === "0" || lockedLimit === 0)
        ) {
            newChallenge.LockedChallengePassword = lockedChallengePassword;
        }
        if (markdownRef.current?.value?.length > 0)
            newChallenge.Markdown = markdownRef.current.value;
        if (image.length > 0 && image[0].src.split(",").length > 1)
            newChallenge.Image = image[0].src.split(",")[1];

        if (challenge.ChallengeType === "CALENDAR") {
            if (halfWayCount != null && halfWayCount !== "0")
                newChallenge.HalfWayCount = parseInt(halfWayCount);
            if (threeQuarterCount != null && threeQuarterCount !== "0")
                newChallenge.ThreeQuarterCount = parseInt(threeQuarterCount);
            if (completeCount != null && completeCount !== "0")
                newChallenge.CompleteCount = parseInt(completeCount);
            if (completeMetricCount != null && completeMetricCount !== "0")
                newChallenge.Metric = parseFloat(completeMetricCount);
            if (metricDescription != null && metricDescription !== "0")
                newChallenge.MetricDescription = metricDescription;
        }

        if (newChallenge.ChallengeType === "ENTERASYOUGO") {
            newChallenge.Placeholder =
                placeholderRef.current?.value?.length > 0
                    ? placeholderRef.current.value
                    : "Add Item";
            newChallenge.InputCount = parseInt(inputCountRef.current.value);
        } else if (
            newChallenge.ChallengeType === "BASIC" ||
            newChallenge.ChallengeType === "BASICINTERVAL"
        ) {
            newChallenge.ChallengeItems = challenge.ChallengeItems.map((c, index) => ({
                ...c,
                index,
            }));
            if (newChallenge.ChallengeType === "BASICINTERVAL")
                newChallenge.Interval = challenge.Interval;
        } else if (newChallenge.ChallengeType === "CATEGORIES") {
            newChallenge.CategoryItems = challenge.CategoryItems.map(cat => ({
                Category: cat.Category,
                ChallengeItems: cat.ChallengeItems.map((ch, index) => ({ ...ch, index })),
            }));
        }

        currentSession()
            .then(user => {
                const isNew = !newChallenge.ChallengeId || newChallenge.ChallengeId.length === 0;
                const url = isNew
                    ? `${config.BASE_URL}/challenge`
                    : `${config.BASE_URL}/challenge/${ChallengeId}`;
                const method = isNew ? "POST" : "PUT";
                if (isNew) delete newChallenge.ChallengeId;

                fetch(url, {
                    method,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user.getIdToken().getJwtToken()}`,
                    },
                    body: JSON.stringify(newChallenge),
                })
                    .then(res => {
                        if (!res.ok) {
                            toast.error(`Something went wrong — status: ${res.status}`);
                            res.json()
                                .then(r => toast.error(`Something went wrong — ${JSON.stringify(r)}`))
                                .catch(err => alert(err));
                            return;
                        }
                        res.json()
                            .then(response => {
                                toast.success(
                                    `Challenge ${ChallengeId ? "edited" : "created"} successfully — id: ${response.ChallengeId}`
                                );
                                setChallengeId(response.ChallengeId);
                                if (response.IsLocked && response.IsLockedLimit != null) {
                                    currentSession()
                                        .then(u => {
                                            fetch(
                                                `${config.BASE_URL}/challenge/passcodes/${ChallengeId}`,
                                                {
                                                    method: "GET",
                                                    headers: {
                                                        "Content-Type": "application/json",
                                                        Authorization: `Bearer ${u.getIdToken().getJwtToken()}`,
                                                    },
                                                }
                                            )
                                                .then(r => r.json())
                                                .then(r => setPasscodes(r))
                                                .catch(console.log);
                                        })
                                        .catch(console.log);
                                }
                            })
                            .catch(err => alert(err));
                    })
                    .catch(err => alert(err));
            })
            .catch(err => toast.error(String(err)));
    };

    // ---------------------------------------------------------------------------
    // Image / CSV handlers
    // ---------------------------------------------------------------------------
    const onDrop = useCallback(acceptedFiles => {
        acceptedFiles.forEach(file => {
            const reader = new FileReader();
            reader.onload = e =>
                setImage([{ id: crypto.randomUUID(), src: e.target.result }]);
            reader.readAsDataURL(file);
        });
    }, []);

    const onDropCSV = useCallback(
        acceptedFiles => {
            acceptedFiles.forEach(file => {
                const reader = new FileReader();
                reader.onload = e => processCsvUpload(e.target.result);
                reader.readAsText(file);
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [challenge]
    );

    const processCsvUpload = file => {
        const data = Papa.parse(file);
        if (file.split("\n")[0].split(",")[0] === "Category") {
            const newItems = [];
            let currentCategory = "";
            let categoryItem = { Category: "", ChallengeItems: [] };
            data.data.forEach((row, ind) => {
                if (ind === 0) return;
                const info = extractInfo(row.slice(2));
                if (row[0].trim() !== currentCategory) {
                    if (currentCategory !== "") newItems.push(categoryItem);
                    currentCategory = row[0].trim();
                    categoryItem = { Category: row[0].trim(), ChallengeItems: [] };
                }
                categoryItem = {
                    ...categoryItem,
                    ChallengeItems: categoryItem.ChallengeItems.concat(
                        info
                            ? { item: row[1], id: randomAlphanumeric(10), info }
                            : { item: row[1], id: randomAlphanumeric(10) }
                    ),
                };
            });
            newItems.push(categoryItem);
            setChallenge({
                ...challenge,
                ChallengeType: "CATEGORIES",
                CategoryItems: newItems,
                ChallengeItems: [],
            });
        } else {
            const newItems = [];
            data.data.forEach((row, ind) => {
                if (row.length > 0 && ind > 0) {
                    const info = extractInfo(row.slice(1));
                    newItems.push(
                        info
                            ? { item: row[0], id: randomAlphanumeric(10), info }
                            : { item: row[0], id: randomAlphanumeric(10) }
                    );
                }
            });
            setChallenge({ ...challenge, ChallengeItems: [...newItems] });
        }
    };

    const deleteImage = () => {
        setChallenge({ ...challenge, Image: "" });
        setImage([]);
    };

    // ---------------------------------------------------------------------------
    // Challenge type / progress handlers
    // ---------------------------------------------------------------------------
    const onTypeChange = ctype => {
        if (challenge.ChallengeType === "BASIC" || challenge.ChallengeType === "BASICINTERVAL")
            setChallengeItemsBackup(challenge.ChallengeItems);
        if (challenge.ChallengeType === "CATEGORIES")
            setCategoryItemsBackup(challenge.CategoryItems);

        if (ctype === "CATEGORIES") {
            setChallenge({
                ...challenge,
                ChallengeType: ctype,
                ChallengeItems: [],
                CategoryItems:
                    challenge.CategoryItems.length === 0 ? categoryItemsBackup : challenge.CategoryItems,
            });
        } else if (ctype === "ENTERASYOUGO" || ctype === "CALENDAR") {
            setChallenge({ ...challenge, ChallengeType: ctype, ChallengeItems: [], CategoryItems: [] });
        } else {
            setChallenge({
                ...challenge,
                ChallengeType: ctype,
                CategoryItems: [],
                ChallengeItems:
                    challenge.ChallengeItems.length === 0 ? challengeItemsBackup : challenge.ChallengeItems,
            });
        }
    };

    const onChangeProgress = pt => setChallenge({ ...challenge, ProgressType: pt });
    const onIntervalChange = interval => setChallenge({ ...challenge, Interval: interval });

    // ---------------------------------------------------------------------------
    // Toggle handlers
    // ---------------------------------------------------------------------------
    const toggleActive = () => setChallenge({ ...challenge, IsActive: !challenge.IsActive });
    const toggleSearchable = () => setChallenge({ ...challenge, IsSearchable: !challenge.IsSearchable });
    const toggleExpired = () => setChallenge({ ...challenge, IsExpired: !challenge.IsExpired });
    const toggleAffiliate = () => setChallenge({ ...challenge, Affiliate: !challenge.Affiliate });
    const toggleLocked = () => setChallenge({ ...challenge, IsLocked: !challenge.IsLocked });
    const setChallengeLiveAt = date => setChallenge({ ...challenge, LiveAt: date });

    // ---------------------------------------------------------------------------
    // Basic challenge item handlers
    // ---------------------------------------------------------------------------
    const handleRemoveChallengeItem = idx => () => {
        if (idx === 0) return;
        setChallenge({
            ...challenge,
            ChallengeItems: challenge.ChallengeItems.filter((_, i) => i !== idx),
        });
    };
    const handleInsertChallengeItem = idx => () => {
        setChallenge({
            ...challenge,
            ChallengeItems: [
                ...challenge.ChallengeItems.slice(0, idx + 1),
                newItem(),
                ...challenge.ChallengeItems.slice(idx + 1),
            ],
        });
    };
    const handleMoveUpChallengeItem = idx => () => {
        if (idx === 0) return;
        const items = [...challenge.ChallengeItems];
        [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
        setChallenge({ ...challenge, ChallengeItems: items });
    };
    const handleMoveDownChallengeItem = idx => () => {
        if (idx === challenge.ChallengeItems.length - 1) return;
        const items = [...challenge.ChallengeItems];
        [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]];
        setChallenge({ ...challenge, ChallengeItems: items });
    };
    const handleChangeChallengeItem = idx => evt => {
        setChallenge({
            ...challenge,
            ChallengeItems: challenge.ChallengeItems.map((item, i) =>
                i === idx ? { ...item, item: evt.target.value } : item
            ),
        });
    };

    // ---------------------------------------------------------------------------
    // Category item handlers
    // ---------------------------------------------------------------------------
    const handleRemoveCategoryItem = idx => () => {
        if (idx === 0) return;
        setChallenge({
            ...challenge,
            CategoryItems: challenge.CategoryItems.filter((_, i) => i !== idx),
        });
    };
    const handleInsertCategoryItem = idx => () => {
        setChallenge({
            ...challenge,
            CategoryItems: [
                ...challenge.CategoryItems.slice(0, idx + 1),
                { Category: "", ChallengeItems: [newItem()] },
                ...challenge.CategoryItems.slice(idx + 1),
            ],
        });
    };
    const handleMoveUpCategoryItem = idx => () => {
        if (idx === 0) return;
        const items = [...challenge.CategoryItems];
        [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
        setChallenge({ ...challenge, CategoryItems: items });
    };
    const handleMoveDownCategoryItem = idx => () => {
        if (idx === challenge.CategoryItems.length - 1) return;
        const items = [...challenge.CategoryItems];
        [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]];
        setChallenge({ ...challenge, CategoryItems: items });
    };
    const handleChangeCategoryItem = idx => evt => {
        setChallenge({
            ...challenge,
            CategoryItems: challenge.CategoryItems.map((cat, i) =>
                i === idx ? { ...cat, Category: evt.target.value } : cat
            ),
        });
    };

    // Category challenge item handlers
    const handleRemoveCategoryChallengeItem = (idx, idc) => () => {
        setChallenge({
            ...challenge,
            CategoryItems: challenge.CategoryItems.map((cat, i) =>
                i !== idx
                    ? cat
                    : { ...cat, ChallengeItems: cat.ChallengeItems.filter((_, j) => j !== idc) }
            ),
        });
    };
    const handleInsertCategoryChallengeItem = (idx, idc) => () => {
        setChallenge({
            ...challenge,
            CategoryItems: challenge.CategoryItems.map((cat, i) =>
                i !== idx
                    ? cat
                    : {
                          ...cat,
                          ChallengeItems: [
                              ...cat.ChallengeItems.slice(0, idc + 1),
                              newItem(),
                              ...cat.ChallengeItems.slice(idc + 1),
                          ],
                      }
            ),
        });
    };
    const handleMoveUpCategoryChallengeItem = (idx, idc) => () => {
        const moveUpAcrossCategory = idc === 0 && idx !== 0;
        const itemToMove = challenge.CategoryItems[idx].ChallengeItems[idc];
        if (idx === 0 && idc === 0) return;
        setChallenge({
            ...challenge,
            CategoryItems: challenge.CategoryItems.map((cat, sidx) => {
                if (sidx !== idx) {
                    if (moveUpAcrossCategory && sidx === idx - 1)
                        return { ...cat, ChallengeItems: [...cat.ChallengeItems, itemToMove] };
                    return cat;
                }
                if (idc === 0) {
                    return {
                        ...cat,
                        ChallengeItems:
                            cat.ChallengeItems.length === 1
                                ? [newItem()]
                                : cat.ChallengeItems.slice(1),
                    };
                }
                const items = [...cat.ChallengeItems];
                [items[idc - 1], items[idc]] = [items[idc], items[idc - 1]];
                return { ...cat, ChallengeItems: items };
            }),
        });
    };
    const handleMoveDownCategoryChallengeItem = (idx, idc) => () => {
        let movingDown = false;
        let itemToMove = null;
        setChallenge({
            ...challenge,
            CategoryItems: challenge.CategoryItems.map((cat, sidx) => {
                if (sidx !== idx) {
                    if (movingDown && sidx === idx + 1)
                        return { ...cat, ChallengeItems: [itemToMove, ...cat.ChallengeItems] };
                    return cat;
                }
                if (idc === cat.ChallengeItems.length - 1) {
                    movingDown = true;
                    itemToMove = cat.ChallengeItems[idc];
                    return {
                        ...cat,
                        ChallengeItems:
                            cat.ChallengeItems.length === 1
                                ? [newItem()]
                                : cat.ChallengeItems.slice(0, -1),
                    };
                }
                const items = [...cat.ChallengeItems];
                [items[idc], items[idc + 1]] = [items[idc + 1], items[idc]];
                return { ...cat, ChallengeItems: items };
            }),
        });
    };
    const handleChangeCategoryChallengeItem = (idx, idc) => evt => {
        setChallenge({
            ...challenge,
            CategoryItems: challenge.CategoryItems.map((cat, i) =>
                i !== idx
                    ? cat
                    : {
                          ...cat,
                          ChallengeItems: cat.ChallengeItems.map((item, j) =>
                              j === idc ? { ...item, item: evt.target.value } : item
                          ),
                      }
            ),
        });
    };

    // ---------------------------------------------------------------------------
    // Item info modal handlers
    // ---------------------------------------------------------------------------
    const getCurrentItemText = () => {
        if (
            challenge.ChallengeType === "CALENDAR" ||
            challenge.ChallengeType === "ENTERASYOUGO"
        )
            return "";
        if (challenge.ChallengeType === "CATEGORIES") {
            return (
                challenge.CategoryItems[infoCategoryIndex]?.ChallengeItems[infoIndex]?.item ?? ""
            );
        }
        return challenge.ChallengeItems[infoIndex]?.item ?? "";
    };

    const openItemInfo = idx => {
        setInfoIndex(idx);
        const item = challenge.ChallengeItems[idx];
        setInfo(item?.info ?? {});
        setExtraInfo(item?.info?.misc ?? []);
        setShowInfoModal(true);
    };

    const openCategoryItemInfo = (idx, idc, item) => {
        setInfoCategoryIndex(idx);
        setInfoIndex(idc);
        setInfo(item?.info ?? {});
        setExtraInfo(item?.info?.misc ?? []);
        setShowInfoModal(true);
    };

    const handleInfoChange = field => evt => {
        setInfo(prev => {
            const next = { ...prev };
            if (evt.target.value.length === 0) delete next[field];
            else next[field] = evt.target.value;
            return next;
        });
    };

    const handleExtraInfoChange = (idx, field) => evt => {
        const value = evt.target.value;
        setExtraInfo(prev => {
            const existing = prev.find(x => x.index === idx);
            if (!existing) {
                return prev.concat({ index: idx, key: "", value: "", [field]: value });
            }
            const updated = { ...existing, [field]: value };
            if (updated.key.length === 0 && updated.value.length === 0)
                return prev.filter(x => x.index !== idx);
            return prev.map(x => (x.index === idx ? updated : x));
        });
    };

    const handleExtraInfoKeyChange = idx => handleExtraInfoChange(idx, "key");
    const handleExtraInfoValueChange = idx => handleExtraInfoChange(idx, "value");

    const handleSubmitItemInfo = () => {
        const hasInvalidPair =
            extraInfo.some(
                x =>
                    (x.key.length > 0 && x.value.length === 0) ||
                    (x.key.length === 0 && x.value.length > 0)
            ) ||
            (info.URL?.length > 0 && !info.URLText?.length) ||
            (!info.URL?.length && info.URLText?.length > 0);

        if (hasInvalidPair) {
            toast.error("Form not valid — please complete every key/value pair.");
            return;
        }

        const mergedInfo = { ...info, misc: extraInfo };
        const isEmpty =
            (Object.keys(info).length === 0 ||
                (Object.keys(info).length === 1 && info.misc)) &&
            extraInfo.length === 0;

        if (challenge.ChallengeType !== "CATEGORIES") {
            setChallenge({
                ...challenge,
                ChallengeItems: challenge.ChallengeItems.map((item, i) => {
                    if (i !== infoIndex) return item;
                    if (isEmpty) {
                        const next = { ...item };
                        delete next.info;
                        return next;
                    }
                    return { ...item, info: mergedInfo };
                }),
            });
        } else {
            setChallenge({
                ...challenge,
                CategoryItems: challenge.CategoryItems.map((cat, i) => {
                    if (i !== infoCategoryIndex) return cat;
                    return {
                        ...cat,
                        ChallengeItems: cat.ChallengeItems.map((item, j) => {
                            if (j !== infoIndex) return item;
                            if (isEmpty) {
                                const next = { ...item };
                                delete next.info;
                                return next;
                            }
                            const nextInfo = { ...mergedInfo };
                            if (extraInfo.length === 0) delete nextInfo.misc;
                            return { ...item, info: nextInfo };
                        }),
                    };
                }),
            });
        }

        setShowInfoModal(false);
    };

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    if (redirect) return <Navigate to="/login" replace />;

    if (isLoading) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3">
                <Spinner animation="border" role="status" variant="primary" />
                <span className="text-muted">Loading challenge…</span>
            </div>
        );
    }

    const showItems =
        challenge.ChallengeType !== "ENTERASYOUGO" && challenge.ChallengeType !== "CALENDAR";

    return (
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <ItemInfoModal
                show={showInfoModal}
                onHide={() => setShowInfoModal(false)}
                onSave={handleSubmitItemInfo}
                getCurrentItemText={getCurrentItemText}
                info={info}
                handleInfoChange={handleInfoChange}
                extraInfo={extraInfo}
                handleExtraInfoKeyChange={handleExtraInfoKeyChange}
                handleExtraInfoValueChange={handleExtraInfoValueChange}
                modalRef={modalRef}
            />

            <BasicFields
                challenge={challenge}
                setChallenge={setChallenge}
                onTypeChange={onTypeChange}
                onChangeProgress={onChangeProgress}
                onIntervalChange={onIntervalChange}
                descriptionLength={descriptionLength}
                setDescriptionLength={setDescriptionLength}
                challengeNameRef={challengeNameRef}
                descriptionRef={descriptionRef}
                markdownRef={markdownRef}
            />

            <ChallengeSwitches
                challenge={challenge}
                toggleActive={toggleActive}
                toggleSearchable={toggleSearchable}
                toggleExpired={toggleExpired}
                toggleAffiliate={toggleAffiliate}
                toggleLocked={toggleLocked}
                setChallengeLiveAt={setChallengeLiveAt}
                lockedLimit={lockedLimit}
                setLockedLimit={setLockedLimit}
                lockedLimitEditable={lockedLimitEditable}
                lockedChallengePassword={lockedChallengePassword}
                setLockedChallengePassword={setLockedChallengePassword}
            />

            {challenge.ChallengeType === "ENTERASYOUGO" && (
                <EnterAsYouGoFields placeholderRef={placeholderRef} inputCountRef={inputCountRef} />
            )}

            {challenge.ChallengeType === "CALENDAR" && (
                <CalendarFields
                    halfWayCount={halfWayCount}
                    setHalfWayCount={setHalfWayCount}
                    threeQuarterCount={threeQuarterCount}
                    setThreeQuarterCount={setThreeQuarterCount}
                    completeCount={completeCount}
                    setCompleteCount={setCompleteCount}
                    completeMetricCount={completeMetricCount}
                    setCompleteMetricCount={setCompleteMetricCount}
                    metricDescription={metricDescription}
                    setMetricDescription={setMetricDescription}
                />
            )}

            {showItems && (
                <>
                    <ChallengeItemsList
                        challengeItems={challenge.ChallengeItems}
                        onAdd={openItemInfo}
                        onRemove={handleRemoveChallengeItem}
                        onInsert={handleInsertChallengeItem}
                        onMoveUp={handleMoveUpChallengeItem}
                        onMoveDown={handleMoveDownChallengeItem}
                        onChange={handleChangeChallengeItem}
                    />
                    <CategoryItemsList
                        categoryItems={challenge.CategoryItems}
                        onCategoryRemove={handleRemoveCategoryItem}
                        onCategoryInsert={handleInsertCategoryItem}
                        onCategoryMoveUp={handleMoveUpCategoryItem}
                        onCategoryMoveDown={handleMoveDownCategoryItem}
                        onCategoryChange={handleChangeCategoryItem}
                        onItemRemove={handleRemoveCategoryChallengeItem}
                        onItemInsert={handleInsertCategoryChallengeItem}
                        onItemMoveUp={handleMoveUpCategoryChallengeItem}
                        onItemMoveDown={handleMoveDownCategoryChallengeItem}
                        onItemChange={handleChangeCategoryChallengeItem}
                        onItemInfo={openCategoryItemInfo}
                    />
                </>
            )}

            <ImageSection
                image={image}
                onDrop={onDrop}
                onDelete={deleteImage}
                onDropCSV={onDropCSV}
            />

            {passcodes != null && (
                <Row className="mb-3">
                    <Form.Group as={Col} md="10" controlId="passcodesControl">
                        <Form.Label>Passcodes</Form.Label>
                        <Form.Control
                            rows={10}
                            as="textarea"
                            disabled
                            value={passcodes.join("\n")}
                            name="Passcodes"
                        />
                    </Form.Group>
                </Row>
            )}

            <Row className="mt-4">
                <Col>
                    <Button type="submit" variant="primary" size="lg">
                        {ChallengeId ? "Update Challenge" : "Create Challenge"}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default CreateChallenge;
